import { PureComponent, memo } from 'react';
import styled, { css } from 'styled-components';
import theme from 'styled-theming';
import { Helmet } from 'react-helmet';
import queryString from 'query-string';
import { ButtonRounded, ButtonRoundedSecondary } from '@comeon/ui';
import { insertInitialMetaForFrame, insertTrackEvent } from '@comeon/analytics';
import { updateMainContentLoading } from '~/components/brand/ducks/index.js';
import breakpoint, { getZindexByType } from '../../utils/style-utils';
import { Smaller, Bigger } from '../layout/mediaquery';
import * as Paths from '../router/constants.js';
import { getBasePathName } from '~/utils/pathname';
import ThemeContext from '../theme/theme-context.js';
import useRouteMap from '../router/use-route-map';
import Link from '../router/query-encoded-link.js';
import { showSupportDialogBox, setVIPChatTagCVValue } from '../support/ducks/index.js';
import useUIContext from '~/ui/use-ui-context';
import ProductNav from '../misc/product-nav';

/* TODO : refactor this, this is quick fix to use the new image optimization pipeline on landing page bg image
 for default images we just add ?format=auto, but for others we remove the width from url and add it as query param
 like ?format=auto&width=640. This is a quick fix and wont work for all widths out of the box */
function optimizeBackgroundImage(url) {
	const widthRegex = /(\d+w)\//;
	const widthMatch = url.match(widthRegex);
	const width = widthMatch ? widthMatch[1].replace('w', '') : null;
	const urlWithoutWidth = url.replace(widthRegex, '');
	if (width) {
		return `${urlWithoutWidth}?format=auto&width=${width}`;
	}
	return `${url}?format=auto`;
}

const Wrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	min-height: 100vh;
	background-position: ${props => {
		if (props.backgroundFocus === 'TR') {
			return 'top right';
		}
		if (props.backgroundFocus === 'TL') {
			return 'top left';
		}
		if (props.backgroundFocus === 'BR') {
			return 'bottom right';
		}
		if (props.backgroundFocus === 'BL') {
			return 'bottom left';
		}
		return 'center';
	}};
	background-size: cover;
	background-repeat: no-repeat;
	background-color: var(--landing-default-bg-color);
	background-image: ${props =>
		props.background?.tiny_background && `url(${optimizeBackgroundImage(props.background.tiny_background)})`};
	${breakpoint.md`
		@media  (orientation: landscape) {
		   height: 100%;
		}
	`};
	${breakpoint.lg`
		height: 100vh;
		background-image: ${props =>
			props.background?.small_background && `url(${optimizeBackgroundImage(props.background.small_background)})`};
			`};
	${breakpoint.xl`
		background-image: ${props =>
			props.background?.medium_background &&
			`url(${optimizeBackgroundImage(props.background.medium_background)})`};
			`};
	${breakpoint.xxl`
		background-image: ${props =>
			props.background.large_background && `url(${optimizeBackgroundImage(props.background.large_background)})`};
			`};
	/* BANJO-13499 Hack for some brands to load better resolution image */
	background-image: ${props =>
		props.forceDefaultBackgroundImage && `url(${optimizeBackgroundImage(props.background.default)}) !important`};
	@media (orientation: portrait) and (max-width: 415px) {
		align-items: normal;
	}
`;

const Intro = styled.div`
	width: 100%;
	padding: 12px;
	position: relative;
	padding-bottom: 76px;
	${breakpoint.lg`
		padding-bottom: calc(100vh / 6);
		width: 620px;
	`}
`;

const LogoWrapper = styled.div.attrs({ 'data-at': 'logo-wrapper-homepage' })`
	margin: 0 auto;
	width: var(--logo-wrapper-width, 50%);
	max-width: 320px;
	margin-bottom: var(--langing-page-logo-mb, 12px);
	svg {
		fill: var(--landing-page-logo, rgba(var(--base-02), 1));
		margin: 0 auto;
	}
	${breakpoint.sm`
		@media  (orientation: landscape) {
		   width: 20%;
		}
	`}
	${breakpoint.md`
		width: var(--logo-wrapper-width, 50%);
		@media  (orientation: landscape) {
		   width: 20%;
		}
	`}
	${breakpoint.lg`
		width: var(--logo-wrapper-width, 50%);
		margin-bottom: 24px;
		@media  (orientation: landscape) {
		   	width: var(--logo-wrapper-width, 50%);
		   	margin-bottom: var(--langing-page-logo-mb, 12px);
		   	@media (max-height: 730px) {
		   		margin-top: ${props => (props.oneLinkEnabled || props.isOntarioRegulatedFranchise ? '0px' : '112px')};
			}
		}
	`};
	@media (orientation: portrait) and (max-width: 415px) {
		margin-top: var(--landing-page-logo-mt, 76px);
	}
`;

const IGOLogo = () => (
	<IGOLogoWrapper>
		<span className="licenses-logos__igo u-dib" data-at="igo-logo" />
	</IGOLogoWrapper>
);

const Heading = styled.h1`
	color: var(--landing-page-title, rgba(var(--base-02), 1));
	font: var(--langing-page-header, var(--h1));
	text-align: center;
	margin: ${props => (props.adjustMargin ? `0 auto 16px auto` : `0 auto 48px auto`)};
	${breakpoint.sm`
		@media  (orientation: portrait) {
			font: var(--langing-page-header-sm, var(--h1));
		}
		@media  (orientation: landscape) {
		  margin: 0 auto 16px auto;
		}
	`}
	${breakpoint.md`
		@media  (orientation: portrait) {
			font: var(--langing-page-header-md, var(--h1));
		}
		@media  (orientation: landscape) {
		  margin: 0 auto 16px auto;
		  max-width: 100%;
		}
	`}
	${breakpoint.xl`
		margin: ${props => (props.adjustMargin ? `0 auto 24px auto` : `0 auto 48px auto`)};
	`}
`;

const HeadingSection = styled.div`
	color: inherit;
	font: var(--landing-page-subtitle, inherit);
`;

const CTAButtonWrapper = styled.div`
	width: 100%;
	padding: 14px;
	max-width: 100%;
`;

const PlayAsGuestButton = memo(styled(ButtonRoundedSecondary)`
	background-color: var(--button-pag, rgba(var(--base-08), 1));
	margin: 8px 8px;
	padding: 14px;
	width: 100%;
	max-width: 240px;
`);

const ButtonRoundedPlayNow = styled(ButtonRounded)`
	background-color: var(--landing-page-cta-btn-bg, var(--button-primary-bg));
	color: var(--landing-page-cta-btn-color, var(--button-primary-text));
	padding: 14px;
	margin: 8px 8px;
	width: 100%;
	max-width: 240px;
`;

const FixedTermsButtonWrapper = styled.div.attrs({
	'data-at': 'mobile-wrapper-terms-landingpage'
})`
	background-color: var(--terms-and-condition_bg, rgba(var(--base-10), 1));
	position: fixed;
	bottom: 0;
	left: 0;
	padding: 11px 12px 12px 12px;
	width: ${props => (props.sidebar && !props.smaller ? `calc(100% - var(--detailWidth))` : '100%')};
	z-index: ${getZindexByType('footer-terms')};
	display: flex;
	opacity: 0.85;
`;

const FixedLoginButton = styled(ButtonRoundedSecondary)`
	background-color: var(--login-btn-bg, rgba(var(--secondary-light), 1));
	color: var(--login-btn-text, rgba(var(--base-02), 1));
	font: var(--label-bold);
	padding: 12px 32px;
	position: fixed;
	right: 24px;
	top: ${props => (props.hasRegulationHeader ? `calc(var(--regulationHeight) + 12px)` : '12px')};
	z-index: 16;
`;

const ProductLinkButtonWrapper = styled.div`
	text-align: center;
	@media (orientation: landscape) and (max-width: 823px) {
		${props =>
			props.oneButtonFlow
				? `
					position: fixed;
					right: 12px;
					padding: 6px !important;
					button {
						font: var(--small);
					}
					top: ${props.hasRegulationHeader ? `calc(var(--regulationHeight) + 12px)` : '12px'};
				`
				: ''}
	}
`;
const subTitleBrandException = theme('brand', {
	pzbuk: css`
		font-weight: 500;
	`
});

const BonusTermsSnippetWrapper = styled.div`
	text-align: center;
	max-width: 100%;
	margin: auto;
	h3,
	p {
		color: var(--bonus-terms-text, rgba(var(--base-02), 1));
	}
	h3 {
		${subTitleBrandException}
	}
`;

const BonusTermsSnippetText = styled.p`
	max-width: 967px;
	margin: 4px auto;
	font-size: 1.2rem;
	font: var(--caption);
`;

const OverlayMobile = styled.div`
	background-color: #202020;
	opacity: 0.7;
	height: 100%;
	position: absolute;
	top: 0;
	bottom: 0;
	width: 100%;
`;

const BonusLink = styled.a`
	color: var(--bonus-terms-link, rgba(var(--base-02), 1));
	text-decoration: underline;
`;

const IGOLogoWrapper = styled.div`
	text-align: center;
`;

const StyledProductNav = styled(ProductNav)`
	margin: 0 auto;

	${breakpoint.lg`
		width: 75%;
		margin: 32px auto;
	`};
`;

const killEvent = e => {
	if (e) {
		e.stopPropagation();
		e.preventDefault();
	}
};

const CTAButtons = memo(({ showLoginAsGuestOption, playAsGuestTitleText, url, dataAt, text, push, oneButtonFlow }) => (
	<CTAButtonWrapper data-at="mobile-wrapper-navbar-landingpage" className="u-ma u-text-c">
		<ButtonRoundedPlayNow
			className="u-mla u-mra"
			onClick={e => {
				killEvent(e);
				push(url);
			}}
			dataAt={dataAt}
		>
			{text}
		</ButtonRoundedPlayNow>

		{showLoginAsGuestOption && !oneButtonFlow && (
			<PlayAsGuestButton
				onClick={e => {
					killEvent(e);
					push('/login/guest');
				}}
				dataAt="pag-button-homepage"
			>
				{playAsGuestTitleText}
			</PlayAsGuestButton>
		)}
	</CTAButtonWrapper>
));

const HomeWrapper = ({
	isDesktop,
	useMobileLandingPageOverlay,
	background,
	bonusTermsSnippet,
	backgroundFocus,
	forceDefaultBackgroundImage,
	children
}) => (
	<Wrapper
		background={background}
		bonusTermsSnippet={bonusTermsSnippet}
		backgroundFocus={backgroundFocus}
		forceDefaultBackgroundImage={forceDefaultBackgroundImage}
	>
		{!isDesktop && useMobileLandingPageOverlay && <OverlayMobile />}
		{children}
	</Wrapper>
);

const dataLayerLoginEvent = () => {
	insertTrackEvent({
		category: 'Login',
		action: 'Click - Log-In',
		label: 'Start Page'
	});
};

const isForcedLanding = () => {
	const onMountQueryString = queryString.parse(window.location.search);
	return onMountQueryString.lp && onMountQueryString.lp === '1';
};

class Home extends PureComponent {
	state = {
		trustlyButtonLoading: false,
		sidebarOpen: false,
		forced: false
	};

	componentDidMount() {
		const { location, dispatch } = this.props;

		// Check if sidebar is open when the page loads
		const onMountQueryString = queryString.parse(location.search);
		if (onMountQueryString.sidebar && onMountQueryString.sidebar.length > 0) {
			this.setState({
				sidebarOpen: true
			});
		}
		if (isForcedLanding()) {
			this.setState({ forced: true });
		}
		insertInitialMetaForFrame('start', dispatch, eventInfo => {
			const parsedData = JSON.parse(eventInfo.replace(/'/g, '"'));
			if (parsedData.pixel_cv) {
				dispatch(setVIPChatTagCVValue(Number.parseInt(parsedData.pixel_cv, 10)));
			}
		});
		dispatch(updateMainContentLoading('loaded'));
	}

	componentDidUpdate() {
		// for registration, redirection handled by session-criteria.js, so skip this in that case
		if (
			this.props.authenticated &&
			!this.props.account.isRegistration &&
			!this.props.smaller &&
			!this.state.forced
		) {
			this.props.transformPathAndPush(this.props.redirectPath);
			return;
		}

		const newQueryString = queryString.parse(this.props.location.search);

		if (newQueryString.sidebar) {
			this.setState({
				sidebarOpen: true
			});
		} else {
			this.setState({
				sidebarOpen: false
			});
		}
	}

	componentWillUnmount() {
		const { supportLoaded } = this.props;
		if (supportLoaded) {
			this.props.dispatch(showSupportDialogBox(false));
		}
	}

	pushLogin = e => {
		killEvent(e);
		this.props.transformPathAndPush(Paths.ROUTE_LOGIN);
		dataLayerLoginEvent();
	};

	trustlyDeposit = async () => {
		insertTrackEvent({
			category: 'Login',
			action: 'Click - Log-In',
			label: 'Start Page'
		});
		this.props.redirectToExternalLogin();
	};

	render() {
		const {
			authenticated,
			brand: {
				background,
				bonusTermsSnippet,
				bonusTerms,
				promoText1,
				promoText2,
				promoText3,
				menu: { landingPage: landingPageMenu },
				isDesktop,
				useMobileLandingPageOverlay,
				backgroundFocus,
				onelinkEnabled, // refers to  showing one button instead of all product buttons
				onelinkPage, // the product onelinkEnabled button will navigate to
				showLoginAsGuestOption,
				globalMenuPromotionLink,
				defaultProduct,
				oneBankIDFlowEnabled,
				oneButtonFlowEnabled,
				isOntarioRegulatedFranchise,
				siteCode
			},
			translations,
			translations: {
				MOBILE_LOGIN_LOGIN_BUTTON,
				MOBILE_LOGIN_REGISTRATION,
				MOBILE_TRUSTLY_DEPOSIT_LOGIN,
				META_KEYWORDS_START,
				META_DESCRIPTION_START,
				MOBILE_PAG_LOGIN,
				LANDING_PAGE_BONUS_TC_LINK_TEXT,
				MOBILE_VISIT_PRODUCT,
				LANDING_PAGE_TERMS_CONDITIONS_TITLE,
				MOBILE_PLAY_NOW
			},
			configuration,
			transformPathAndPush,
			transformPathAndReplace,
			routesMap
		} = this.props;
		const oneButtonFlow = oneBankIDFlowEnabled || oneButtonFlowEnabled;

		const { isAnonymous, license } = configuration;
		const { trustlyButtonLoading } = this.state;
		const primaryCTA = {
			url: oneBankIDFlowEnabled
				? Paths.ROUTE_PLAY_NOW_BANKID
				: oneButtonFlowEnabled
					? Paths.ROUTE_PLAY_NOW_DGA
					: isAnonymous
						? Paths.ROUTE_GUEST_DEPOSIT_LOGIN
						: Paths.ROUTE_REGISTER,
			dataAt: oneButtonFlow
				? 'play-now-homepage'
				: isAnonymous
					? 'deposit-and-play-homepage'
					: 'open-account-homepage',
			text: oneButtonFlow
				? MOBILE_PLAY_NOW
				: isAnonymous
					? MOBILE_TRUSTLY_DEPOSIT_LOGIN
					: MOBILE_LOGIN_REGISTRATION
		};
		const { hasStaticHeader } = this.props.uiContext;
		let productsListMobile = landingPageMenu;
		const tvBetIdx = landingPageMenu.findIndex(product => product === 'tvbet');
		if (tvBetIdx > 3) {
			productsListMobile = landingPageMenu.slice(0, 3).concat('tvbet');
		}

		const forceDefaultBackgroundImage = configuration.forceHighQualityBackgroundImageOnStartpage;
		const useSpecialLandingLogo = siteCode === 'lyllo';

		return (
			<HomeWrapper
				isDesktop={isDesktop}
				useMobileLandingPageOverlay={useMobileLandingPageOverlay}
				background={background}
				bonusTermsSnippet={bonusTermsSnippet}
				backgroundFocus={backgroundFocus}
				forceDefaultBackgroundImage={forceDefaultBackgroundImage}
			>
				<Helmet>
					<meta name="keywords" content={META_KEYWORDS_START} />
					<meta name="description" content={META_DESCRIPTION_START} />
					<script type="application/ld+json">{translations.SEO_JSONLD_HOMEPAGE}</script>
				</Helmet>
				<Intro>
					{isOntarioRegulatedFranchise && <IGOLogo />}
					<LogoWrapper isOntarioRegulatedFranchise={isOntarioRegulatedFranchise}>
						<Link href={getBasePathName(license, defaultProduct)()}>
							{useSpecialLandingLogo ? (
								<ThemeContext.Consumer>
									{({ LogoLanding }) => <LogoLanding largeLogo />}
								</ThemeContext.Consumer>
							) : (
								<ThemeContext.Consumer>{({ Logo }) => <Logo largeLogo />}</ThemeContext.Consumer>
							)}
						</Link>
					</LogoWrapper>
					<Heading data-at="heading-homepage" className="heading-homepage" adjustMargin={bonusTerms}>
						{promoText1}
						{promoText2.length > 0 && <HeadingSection>{promoText2}</HeadingSection>}
						{promoText3.length > 0 && <HeadingSection>{promoText3}</HeadingSection>}
					</Heading>
					{!authenticated && (
						<>
							{!oneButtonFlow && (
								<>
									{!isAnonymous && (
										<FixedLoginButton
											auto
											onClick={this.pushLogin}
											dataAt="login-homepage"
											className="login-homepage"
											hasRegulationHeader={hasStaticHeader}
										>
											{MOBILE_LOGIN_LOGIN_BUTTON}
										</FixedLoginButton>
									)}
									{isAnonymous && (
										<FixedLoginButton
											auto
											hasRegulationHeader={hasStaticHeader}
											onClick={
												this.props.configuration.useTrustlyLogin
													? this.trustlyDeposit
													: this.pushLogin
											}
											disabled={trustlyButtonLoading}
											dataAt="login-homepage"
											className="login-homepage"
										>
											{MOBILE_LOGIN_LOGIN_BUTTON}
											{trustlyButtonLoading && <div className="spinner" />}
										</FixedLoginButton>
									)}
								</>
							)}

							<CTAButtons
								showLoginAsGuestOption={showLoginAsGuestOption}
								playAsGuestTitleText={MOBILE_PAG_LOGIN}
								url={primaryCTA.url}
								dataAt={primaryCTA.dataAt}
								text={primaryCTA.text}
								push={transformPathAndPush}
								oneButtonFlow={oneButtonFlow}
							/>
						</>
					)}
					{onelinkEnabled && (
						<ProductLinkButtonWrapper oneButtonFlow={oneButtonFlow} hasRegulationHeader={hasStaticHeader}>
							<ButtonRoundedSecondary
								onClick={e => {
									killEvent(e);
									transformPathAndReplace(routesMap[onelinkPage], null, {
										preserveSidebarState: true
									});
								}}
								className="u-pr2 u-pl2 btn btn--visit"
								auto
								smallfont
								dataAt="onelink-homepage"
							>
								{MOBILE_VISIT_PRODUCT}
							</ButtonRoundedSecondary>
						</ProductLinkButtonWrapper>
					)}
					{!onelinkEnabled && (
						<>
							<Bigger>
								<StyledProductNav
									globalMenuPromotionLink={globalMenuPromotionLink}
									translations={translations}
									products={landingPageMenu}
								/>
							</Bigger>
							<Smaller>
								<StyledProductNav
									globalMenuPromotionLink={globalMenuPromotionLink}
									translations={translations}
									products={productsListMobile}
								/>
							</Smaller>
						</>
					)}
					{(bonusTerms || bonusTermsSnippet.length > 0) && (
						<FixedTermsButtonWrapper
							onClick={e => {
								killEvent(e);
								if (bonusTerms) transformPathAndPush(Paths.ROUTE_TERMS_CONDITIONS_BONUS_SIDEBAR);
							}}
							sidebar={this.state.sidebarOpen}
							smaller={this.props.smaller}
						>
							<BonusTermsSnippetWrapper data-at="bonus-terms-wrapper" className="bonus-terms-wrapper">
								<h3 className="terms-header__text-color">{LANDING_PAGE_TERMS_CONDITIONS_TITLE}</h3>
								<BonusTermsSnippetText>
									{bonusTermsSnippet} &nbsp;
									{bonusTerms && (
										<BonusLink data-at="bonus-terms-link" textAlign="center" mb={[0, 3]}>
											{LANDING_PAGE_BONUS_TC_LINK_TEXT}
										</BonusLink>
									)}
								</BonusTermsSnippetText>
							</BonusTermsSnippetWrapper>
						</FixedTermsButtonWrapper>
					)}
				</Intro>
			</HomeWrapper>
		);
	}
}

const HomeWithUIContext = props => {
	const uiContext = useUIContext();
	const routesMap = useRouteMap();
	return <Home {...props} uiContext={uiContext} routesMap={routesMap} />;
};

export default HomeWithUIContext;
